import { useEffect, useMemo } from 'react';
import Icon from '@ant-design/icons';
import { Form, FormInstance, Select, SelectProps } from 'antd';
import InfoSvg from 'global/Securities/components/Icons/InfoSvg';
import {
  BuyOrderType,
  SellOrderType,
  TransactionType,
} from 'global/Securities/types/transaction';
import { DATA_CY_MODAL_TRADING } from 'global/TradingModal/constant';
import { clevertapAutoOrderOptionsViewed } from 'lib/analytics/securities';
import { InfoButton } from './styled';

interface BuyOrderTypeProps {
  transactionType: TransactionType.Buy;
  defaultValue?: BuyOrderType;
  onChange?(type: BuyOrderType): void;
}

interface SellOrderTypeProps {
  transactionType: TransactionType.Sell;
  defaultValue?: SellOrderType;
  onChange?(type: SellOrderType): void;
}

type SelectOrderType = SelectProps<BuyOrderType | SellOrderType>;

interface OrderTypeFormItemBaseProps {
  form: FormInstance;
  hideInfoIcon?: boolean;
  onInfoIconClick?(): void;
  onChange?: SelectOrderType['onChange'];
}

type OrderTypeFormItemProps = OrderTypeFormItemBaseProps &
  (BuyOrderTypeProps | SellOrderTypeProps);

const orderTypeOptions = {
  [TransactionType.Buy]: {
    [BuyOrderType.LimitOrder]: 'Limit Order',
    [BuyOrderType.AutoBuy]: 'Auto Buy',
  },
  [TransactionType.Sell]: {
    [SellOrderType.LimitOrder]: 'Limit Order',
  },
} as const;

const OrderTypeFormItem = ({
  form,
  transactionType = TransactionType.Buy,
  defaultValue = BuyOrderType.LimitOrder,
  hideInfoIcon = false,
  onInfoIconClick = () => {},
  onChange = () => {},
}: OrderTypeFormItemProps) => {
  const activeOptions = useMemo(
    () => Object.entries(orderTypeOptions[transactionType]),
    [transactionType],
  );

  const handleChange: SelectOrderType['onChange'] = (value, option) => {
    onChange(value, option);
    form.validateFields();
  };

  return (
    <>
      <Form.Item
        label={
          <>
            Order type
            {!hideInfoIcon && (
              <InfoButton
                type="button"
                onClick={onInfoIconClick}
                data-cy={DATA_CY_MODAL_TRADING.ORDER_TYPE_BUTTON_INFO}
              >
                <Icon
                  component={InfoSvg}
                  style={{ fontSize: 16, color: '#969696', marginLeft: 4 }}
                />
              </InfoButton>
            )}
          </>
        }
        name="orderType"
        initialValue={defaultValue}
        hidden={activeOptions.length <= 1}
      >
        <Select
          onChange={handleChange}
          onDropdownVisibleChange={(visible) => {
            if (visible) {
              clevertapAutoOrderOptionsViewed();
            }
          }}
          data-cy={DATA_CY_MODAL_TRADING.ORDER_TYPE_SELECT}
          getPopupContainer={() =>
            document.getElementById(
              DATA_CY_MODAL_TRADING.ORDER_TYPE_SELECT_DROPDOWN,
            )
          }
        >
          {activeOptions.map(([key, text]) => (
            <Select.Option key={key} value={key}>
              {text}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <div
        id={DATA_CY_MODAL_TRADING.ORDER_TYPE_SELECT_DROPDOWN}
        data-cy={DATA_CY_MODAL_TRADING.ORDER_TYPE_SELECT_DROPDOWN}
      />
    </>
  );
};

export default OrderTypeFormItem;
